import React from 'react';
import { Container, Card, Button, Col, Row, Badge } from 'react-bootstrap';
import PricingHeader from './PricingHeader';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

function PricingCards() {
  const styles = {
    general: {
      fontFamily: "'Inter', sans-serif",
      color: '#2C3E50',
    },
    card: {
      border: 'none',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      height: '100%',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      },
    },
    cardHeader: {
      padding: '2rem',
      border: 'none',
      textAlign: 'center',
    },
    price: {
      fontSize: '4rem',
      fontWeight: '700',
      color: '#2D3748',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    priceUnit: {
      fontSize: '1.5rem',
      color: '#718096',
      fontWeight: '500',
    },
    feature: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1rem',
      fontSize: '1.1rem',
      color: '#4A5568',
    },
    featureIcon: {
      color: '#48BB78',
      fontSize: '1.2rem',
    },
    badge: {
      padding: '0.5rem 1rem',
      fontSize: '0.9rem',
      fontWeight: '500',
      marginRight: '0.5rem',
    },
    button: {
      padding: '1rem 2rem',
      fontWeight: '600',
      borderRadius: '8px',
      transition: 'transform 0.2s ease',
      width: '100%',
      marginTop: '1.5rem',
      '&:hover': {
        transform: 'translateY(-2px)',
      },
    },
    popularBadge: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
      color: 'white',
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.9rem',
      fontWeight: '500',
    },
    cardBody: {
      padding: '2rem',
    },
    featureList: {
      margin: '2rem 0',
      padding: 0,
      listStyle: 'none',
    },
  };

  const plans = [
    {
      name: 'Free Plan',
      price: '0',
      features: [
        { text: '50 qpAdm Runs In Total', badge: '50' },
        { text: 'Basic Support', badge: 'Basic' },
        { text: 'Standard Processing', badge: 'Standard' },
      ],
      buttonText: 'Sign up for free',
      buttonVariant: 'outline-primary',
      headerGradient: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)',
      link: '/signup',
      badgeVariant: 'primary',
    },
    {
      name: 'Pro Plan',
      price: '15',
      popular: true,
      features: [
        { text: '200 qpAdm Runs', badge: '200' },
        { text: 'Priority Support', badge: 'Priority' },
        { text: 'Merge Your Own Data', badge: 'Advanced' },
        { text: 'Fast Processing', badge: 'Fast' },
      ],
      buttonText: 'Get Started',
      buttonVariant: 'success',
      headerGradient: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
      disabled: true,
      badgeVariant: 'success',
    },
  ];

  return (
    <>
      <Helmet>
        <title>DNACloudHub | Pricing</title>
        <meta name="description" content="Explore the flexible pricing plans of DNACloudHub, designed to provide access to our cloud-based platform for running qpAdm models and advanced genetic analysis at competitive rates." />
        <meta property="og:title" content={'DNACloudHub | Pricing'} />
        <meta property="og:description" content={'"Explore the flexible pricing plans of DNACloudHub, designed to provide access to our cloud-based platform for running qpAdm models and advanced genetic analysis at competitive rates."'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dnacloudhub.com/pricing`} />
      </Helmet>

      <div style={styles.general}>
        <PricingHeader />

        <Container className="mb-5">
          <Row className="justify-content-center g-4">
            {plans.map((plan, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <Card style={styles.card} className="position-relative">
                    {plan.popular && (
                      <div style={styles.popularBadge}>Most Popular</div>
                    )}
                    <Card.Header
                      style={{
                        ...styles.cardHeader,
                        background: plan.headerGradient,
                      }}
                      className="text-white"
                    >
                      <h3 className="h4 mb-0 fw-bold">{plan.name}</h3>
                    </Card.Header>
                    <Card.Body style={styles.cardBody}>
                      <div style={styles.price}>
                        ${plan.price}
                        <span style={styles.priceUnit}>/mo</span>
                      </div>
                      <ul style={styles.featureList}>
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} style={styles.feature}>
                            <Badge
                              bg={plan.badgeVariant}
                              style={styles.badge}
                            >
                              {feature.badge}
                            </Badge>
                            {feature.text}
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant={plan.buttonVariant}
                        size="lg"
                        as={plan.link ? Link : undefined}
                        to={plan.link}
                        style={styles.button}
                        className="shadow"
                        disabled={plan.disabled}
                      >
                        {plan.buttonText}
                      </Button>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>

        {/* FAQ Section */}
        <Container className="mb-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: '16px',
              padding: '3rem',
              textAlign: 'center',
            }}>
              <h2 style={{ color: '#1a365d', marginBottom: '2rem', fontWeight: '700' }}>
                Have questions about our pricing?
              </h2>
              <p style={{ color: '#4A5568', fontSize: '1.1rem', marginBottom: '2rem', maxWidth: '800px', margin: '0 auto 2rem' }}>
                Our team is here to help you choose the right plan for your needs. Contact us for detailed information about our services and custom solutions.
              </p>
              <Button
                variant="primary"
                size="lg"
                as={Link}
                to="/contact_us"
                style={{
                  padding: '1rem 2rem',
                  fontWeight: '600',
                  borderRadius: '8px',
                }}
                className="shadow"
              >
                Contact Us
              </Button>
            </div>
          </motion.div>
        </Container>
      </div>
    </>
  );
}

export default PricingCards;
